// React i hooks
import { memo, useCallback, useEffect, useState } from "react";

// Helpers
import { useTranslate } from "helpers";
import { useHandleDropdownChange } from "services/forms/hooks";

// API i konstante
import { FleetVehicleSearchFormValues } from "services/api/fleet/vehicles/types";
import { initialFleetVehicleSearchForm } from "services/api/fleet/vehicles/consts";

// UI komponente
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { TableViewToggle } from "ui/components/Table/TableViewToggle";

// Dropdown komponente
import VehicleManufacturerDropdown from "ui/components/Dropdown/VehicleManufacturerDropdown/VehicleManufacturerDropdown";
import VehicleModelDropdown from "ui/components/Dropdown/VehicleModelDropdown/VehicleModelDropdown";
import VehicleTypeDropdown from "ui/components/Dropdown/VehicleTypeDropdown/VehicleTypeDropdown";

// Tipovi
import { MarketplaceSearchFilterProps } from "./types";
import VehicleSearchFiltersModal from "ui/modules/fleet/vehicles/pages/VehicleSearchPage/components/VehicleSearchFilters/VehicleSearchFiltersModal";
import { Chip } from "ui/components/Chip";

const MarketplaceSearchFilters: React.FC<MarketplaceSearchFilterProps> = (props) => {
  const { onSearchChange, viewType, onViewTypeChange } = props;

  const { t } = useTranslate();

  const [formValues, setFormValues] = useState<FleetVehicleSearchFormValues>(
    initialFleetVehicleSearchForm
  );
  const [showModal, setShowModal] = useState<boolean>(false);

  useEffect(() => {
    setFormValues((old) => ({ ...old, vehicleModelId: "", vehicleTypeId: "" }));
  }, [formValues.vehicleManufacturerId]);

  useEffect(() => {
    setFormValues((old) => ({ ...old, vehicleTypeId: "" }));
  }, [formValues.vehicleModelId]);

  useEffect(() => {
    onSearchChange(formValues);
  }, [formValues.advancedSearch]);

  const handleSearchChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const updatedFormValues = { ...formValues, search: e.target.value };
    setFormValues(updatedFormValues);
    onSearchChange(updatedFormValues);
  };

  const handleResetFormClick = useCallback(() => {
    setFormValues(initialFleetVehicleSearchForm);
  }, []);

  const handleAdditionalFilterClick = useCallback(
    (formValues: FleetVehicleSearchFormValues) => {
      setFormValues((prevValues) => ({
        ...prevValues,
        ...formValues,
      }));
      onSearchChange(formValues);
      setShowModal(false);
    },
    [setFormValues, setShowModal]
  );

  const handleRemoveFilterClick = useCallback(
    (id: string, name: string, text: string) => {
      setFormValues((prevValues) => {
        return {
          ...prevValues,
          [name]: null,
          advancedSearch: prevValues.advancedSearch.filter(
            (item) => item.name !== name
          ),
        };
      });
      onSearchChange(formValues);
    },
    [setFormValues]
  );

  const handleDropdownChange = useHandleDropdownChange(
    setFormValues,
    formValues,
    formValues.advancedSearch
  );

  return (
    <>
      <div className="overflow-x-scroll md:overflow-x-visible scrollbar-hide px-4 xl:px-0">
        <div className="flex justify-between items-center ">
          <div className="flex flex-wrap gap-3">
            <div className="">
              <TextField
                type="text"
                name="search"
                value={formValues.search}
                onChange={handleSearchChange}
                placeholder={t("admin.search.placeholder")}
                cssClassName="bg-white"
              />
            </div>
            <div className="hidden gap-3 xl:flex">
              <div className="min-w-[200px]">
                <VehicleManufacturerDropdown
                  placeholder={t("admin.vehicle.manufacturer.placeholder")}
                  name="vehicleManufacturerId"
                  value={formValues.vehicleManufacturerId}
                  onChange={handleDropdownChange}
                  width="full"
                />
              </div>
              <div className="min-w-[200px]">
                <VehicleModelDropdown
                  placeholder={t("admin.vehicle.model.placeholder")}
                  name="vehicleModelId"
                  vehicleManufacturerId={formValues.vehicleManufacturerId}
                  value={formValues.vehicleModelId}
                  onChange={handleDropdownChange}
                  width="full"
                />
              </div>
              <div className="min-w-[200px]">
                <VehicleTypeDropdown
                  placeholder={t("admin.vehicle.type.placeholder")}
                  name="vehicleTypeId"
                  vehicleManufacturerId={formValues.vehicleManufacturerId}
                  vehicleModelId={formValues.vehicleModelId}
                  value={formValues.vehicleTypeId}
                  onChange={handleDropdownChange}
                />
              </div>
            </div>
            <Button
              variant="outlined"
              startIcon="/media/icons/text-search.svg"
              onClick={() => setShowModal(true)}
            />
            <div className="mr-3 md:mr-6 lg:md-12 flex items-center">
              <span
                onClick={handleResetFormClick}
                className="text-xs font-bold cursor-pointer text-gray-700"
              >
                {t("admin.reset")}
              </span>
            </div>
          </div>

          {/* Odabir prikaza stoji u istom redu kao filtri, kao i na pretrazi vozila. */}
          <TableViewToggle
            value={viewType}
            onChange={onViewTypeChange}
            options={[
              { value: "table", label: t("admin.table.viewtoggle.table"), icon: "/media/icons/table.svg" },
              { value: "grid", label: t("admin.table.viewtoggle.grid"), icon: "/media/icons/grid.svg" },
            ]}
          />
        </div>
        <div className="mb-3 flex gap-1">
          {formValues.advancedSearch &&
            formValues.advancedSearch.map((item, index: number) => {
              return (
                <Chip
                  name={item.name}
                  id={item.value}
                  text={`${item.label ? `${item.label}:` : ""}${item.text}`}
                  key={index}
                  onClick={handleRemoveFilterClick}
                />
              );
            })}
        </div>
      </div>
      <VehicleSearchFiltersModal
        show={showModal}
        onSuccess={handleAdditionalFilterClick}
        onClose={() => setShowModal(false)}
        formValues={formValues}
        setFormValues={setFormValues}
      />
    </>
  );
};
export default memo(MarketplaceSearchFilters);
